import { useCallback, useMemo, useState } from 'react'
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  useGetAuthoringComponentQuery,
  useGetManifestQuery,
  useListTenantDomainsQuery,
  type ManifestIntent,
} from '../api/authoring'
import { problemMessage } from '../api/baseApi'
import { payloadId, useIntent, shown } from '../api/useIntent'
import { useSelectedNode } from '../api/useSelectedNode'
import { IntentForm } from '../editors/IntentForm'
import { mainByDefault, siteAddresses } from '../model/domains'
import { allLinkIds, childByLinkId, decodeLinkPath, encodeLinkPath, isLinked, pinStateOf, type LinkPath } from '../model/link'
import { containable, formableIntents, intentTitle, structuralIntents, structuralPayload, typeOf, typeTitle } from '../model/manifest'
import { nameOrId } from '../model/name'
import { routes } from '../model/routes'
import { menusInUse, resolveRouting, routeMenus, websiteFields, SITE_TYPE, type ResolvedRoute, type WebsiteDomain } from '../model/website'
import { ComponentName } from '../ui/ComponentName'
import { CompositionEditor } from '../ui/CompositionEditor'
import { NodePanel } from '../ui/NodePanel'
import { PinChip } from '../ui/PinChip'
import { Loading, QueryError } from '../ui/QueryState'
import { UndoButton } from '../ui/UndoButton'
import { PlacementPicker } from './page/PlacementPicker'
import { RouteDialog } from './site/RouteDialog'
import { GeneratedRouteDialog } from './site/GeneratedRouteDialog'
import { RouteTree } from './site/RouteTree'
import { PublicationStatus } from './site/PublicationStatus'
import { Releases } from './site/Releases'

/**
 * A site, edited.
 *
 * **This is a bespoke editor, and Website is the type that earned one.**
 * Everywhere else in this client a control is derived: the placement
 * controls read their intents by role and their arrangements off the
 * fields schema, and a type nobody anticipated gets both. That derivation
 * stops here, for one reason that is worth stating precisely rather than
 * apologising for.
 *
 * **A payload says what an intent needs, not which arrangement it fills.**
 * With one list per type the two were the same question — a Container has
 * `items` and one intent that places into it. A Website has *three*
 * arrangements and three place intents: `website.default_placed` fills a
 * slot default, `website.page_added` grows the routing tree, and
 * `website.not_found_placed` replaces one link. The slot-carrying one is
 * told apart by its marker; the other two are the same shape and mean
 * entirely different things. Closing that would take a fifth marker
 * naming the field an intent feeds, and this is one example — the same
 * reason **B6** is not being decided on the strength of a single case.
 *
 * Two more things a schema cannot describe, and they are why this screen
 * would exist anyway: a route's URL path is assembled from every slug
 * above it, and a web address is chosen from the hosts the platform
 * operator granted the tenant rather than typed — a payload says the host
 * is a string, not which strings the platform will accept.
 *
 * So the intents are named here. What keeps that from rotting is the last
 * section: anything the type declares that no section above claims is
 * rendered anyway, so a sixteenth Website intent appears the day it is
 * registered rather than being silently dropped.
 *
 * **Four faces, because the release is a Website concern of its own.**
 * They are addresses, like a page's:
 *
 *   Routing  — the tree, and the way to every page in the site. The bare
 *              address, because it is what a site is opened for.
 *   Composition — what an unmatched path composes to, and the header and
 *              footer every page inherits. Both are arrangements of this
 *              document, and they are the two that want the tree-and-panel
 *              layout the other build screens have.
 *   Settings — the web addresses, the theme, the title, the search metadata,
 *              and the safety net. Facts an operator sets when a site is
 *              provisioned and then rarely touches, which is the same
 *              split content mode's page makes.
 *   Releases — the immutable publication history, what is current, and
 *              the actions that capture or roll the whole site back.
 */

/** The intents each section draws, and the only place this screen names any. */
const SECTIONS = {
  identity: ['website.renamed', 'website.retitled', 'website.presentation_set', 'website.seo_set'],
  domains: ['website.domain_bound', 'website.domain_released'],
  routing: [
    'website.page_added',
    'website.page_removed',
    'website.route_updated',
    'website.routes_reordered',
    'website.generated_route_created',
    'website.generated_route_updated',
    'website.generated_route_removed',
    'website.home_set',
  ],
  notFound: ['website.not_found_placed', 'website.not_found_cleared'],
}

const CLAIMED = new Set(Object.values(SECTIONS).flat())

export function SiteEditor({ face = 'routing' }: { face?: 'routing' | 'compose' | 'settings' | 'releases' }) {
  const { tenant = '', site = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const manifest = useGetManifestQuery({ tenant })
  const component = useGetAuthoringComponentQuery({ tenant, id: site })
  const { send, isLoading: sending, error: sendError } = useIntent(tenant)

  // The slot defaults are a tree, so a placement in one is selectable —
  // and it is held in the URL for the reason every other tree's is: a
  // colleague can be sent the block rather than told where to click.
  const selection = decodeLinkPath(params.get('sel'))
  const select = useCallback(
    (path: LinkPath) => {
      const next = new URLSearchParams(params)
      if (path.length === 0) next.delete('sel')
      else next.set('sel', encodeLinkPath(path))
      setParams(next, { replace: true })
    },
    [params, setParams],
  )
  const selected = useSelectedNode(tenant, component.data, selection)

  const [routeDialog, setRouteDialog] = useState<
    { intent: 'website.page_added'; under: string; position: number }
    | { intent: 'website.route_updated'; route: ResolvedRoute }
    | { intent: 'website.generated_route_created'; under: string; position: number; parentPath: string }
    | { intent: 'website.generated_route_updated'; route: ResolvedRoute }
    | null
  >(null)
  const [placingNotFound, setPlacingNotFound] = useState(false)

  const typeName = component.data?.type ?? SITE_TYPE
  const structural = useMemo(
    () => new Map(structuralIntents(manifest.data, typeName).map((intent) => [intent.name, intent])),
    [manifest.data, typeName],
  )

  const sendIntent = useCallback(
    (name: string, payload: Record<string, unknown>) => send(site, name, payload),
    [send, site],
  )

  const routeAction = useCallback(
    (name: string, supplied: Parameters<typeof structuralPayload>[1], rest?: Record<string, unknown>) => {
      const intent = structural.get(name)
      if (!intent) return
      sendIntent(name, structuralPayload(intent, supplied, rest)).catch(shown)
    },
    [sendIntent, structural],
  )

  if (manifest.isLoading || component.isLoading) return <Loading label="Reading the site…" />
  if (component.error) return <QueryError error={component.error} title="Could not read this site" />
  if (!component.data) return null

  const document = component.data
  const fields = websiteFields(document)
  const siteRoutes = resolveRouting(document)
  const notFoundChild = fields.notFound ? childByLinkId(document.children, fields.notFound) : undefined
  const formable = formableIntents(manifest.data, typeName, document.fields ?? {})
  const intentIn = (names: string[]) => formable.filter((intent) => names.includes(intent.name))
  const unclaimed = formable.filter((intent) => !CLAIMED.has(intent.name))

  // The type's own words for each control, with the derivation from the
  // intent's name behind them. This screen names its intents by hand —
  // the reason is at the top of the file — and it must not also name
  // what they are called.
  const label = (name: string) => intentTitle(manifest.data, typeName, name)

  const placeNotFound = (child: Record<string, unknown>) => {
    const intent = structural.get('website.not_found_placed')
    if (!intent) return
    sendIntent(intent.name, structuralPayload(intent, { child })).catch(shown)
    setPlacingNotFound(false)
  }

  return (
    <Stack spacing={4}>
      <Stack spacing={1}>
        <Typography variant="h1">{nameOrId(document, manifest.data)}</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" label={typeTitle(manifest.data, document.type)} />
          <Chip size="small" variant="outlined" label={`${document.id} · r${document.revision}`} />
          {fields.name && <Chip size="small" variant="outlined" label={fields.name} />}
          {document.tombstoned && <Chip size="small" color="error" label="deleted" />}
          <PublicationStatus tenant={tenant} site={site} />
        </Stack>
        <Stack direction="row">
          <UndoButton tenant={tenant} document={document} manifest={manifest.data} disabled={sending} />
        </Stack>

        {/* Links, not buttons — a face is an address. Routing is the
            bare one because it is what a site is opened for and the way
            to every page in it; the theme, the search metadata and the
            hostnames are set when a site is made and then rarely
            touched, which is exactly the split a page makes between
            its blocks and its settings. */}
        <Tabs value={face} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Routing" value="routing" component={RouterLink} to={routes.siteRouting(tenant, site)} />
          <Tab
            label="Composition"
            value="compose"
            component={RouterLink}
            to={routes.siteComposition(tenant, site)}
          />
          <Tab label="Settings" value="settings" component={RouterLink} to={routes.siteSettings(tenant, site)} />
          <Tab label="Releases" value="releases" component={RouterLink} to={routes.siteReleases(tenant, site)} />
        </Tabs>
      </Stack>

      {sendError && <Alert severity="error">{problemMessage(sendError, 'That change could not be made.')}</Alert>}

      {face === 'releases' && <Releases tenant={tenant} site={site} manifest={manifest.data} />}

      {face === 'routing' && (
      <Section
        title="Routing"
        caption="Routing lives on the site, not on the page: a slug belongs to the route that reaches a page, so moving a page in the menu does not revise the page."
      >
        <RouteTree
          routes={siteRoutes}
          tenant={tenant}
          site={site}
          manifest={manifest.data}
          references={document.references ?? []}
          labels={{
            add: label('website.page_added'),
            addGenerated: label('website.generated_route_created'),
            edit: label('website.route_updated'),
            remove: label('website.page_removed'),
            home: label('website.home_set'),
            move: label('website.routes_reordered'),
          }}
          actions={{
            onAddUnder: (route) =>
              setRouteDialog({
                intent: 'website.page_added',
                under: route?.route.page ?? '',
                position: route ? (route.children.length) : siteRoutes.length,
              }),
            onAddGeneratedUnder: (route) => setRouteDialog({ intent: 'website.generated_route_created', under: route?.key ?? '', position: route ? route.children.length : siteRoutes.length, parentPath: route?.urlPath ?? '/' }),
            onEdit: (route) => setRouteDialog(route.generated ? { intent: 'website.generated_route_updated', route } : { intent: 'website.route_updated', route }),
            onRemove: (route) => route.generated ? sendIntent('website.generated_route_removed', { id: route.route.id }).catch(shown) : routeAction('website.page_removed', { link: route.route.page }),
            onSetHome: (route) => routeAction('website.home_set', { link: route.route.page }),
            // `under` names the *level*, which is the page its parent
            // route serves — empty at the top. It is the same member
            // `page_added` uses, and both arrive through the link
            // marker. The tree computes the order, because a drop and a
            // menu step are the same command over the same list and only
            // the tree knows where the row landed.
            onReorder: (under, order) =>
              routeAction('website.routes_reordered', { link: under || undefined, order }),
          }}
          busy={sending}
        />
      </Section>
      )}

      {face === 'compose' && (
        <>
      <Section
        title="Not found"
        caption="What an unmatched path composes to — ordinary content, so a 404 is branded like any other page. It replaces whatever stood there, rather than adding to a list."
      >
        <Paper sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {notFoundChild ? (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={notFoundChild.linkId} />
                <Typography variant="body2">{typeTitle(manifest.data, notFoundChild.type)}</Typography>
                {isLinked(notFoundChild) && notFoundChild.id && (
                  <ComponentName
                    tenant={tenant}
                    id={notFoundChild.id}
                    revision={notFoundChild.revision}
                    manifest={manifest.data}
                    prefix="→ "
                  />
                )}
                <PinChip pin={pinStateOf(notFoundChild)} />
              </Stack>
            ) : (
              <Typography color="text.secondary" variant="body2">
                Unset — an unmatched path composes to nothing.
              </Typography>
            )}

            {/* `not_found_cleared` states nothing at all, so it is a
                button rather than a form — which is what an empty payload
                schema means and what IntentForm already draws for one. */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {intentIn(SECTIONS.notFound).map((intent) => (
                <IntentForm
                  key={intent.name}
                  intent={intent}
                  fields={fields as Record<string, unknown>}
                  busy={sending}
                  send={sendIntent}
                />
              ))}
            </Stack>

            <Stack direction="row">
              <Button size="small" startIcon={<AddIcon />} disabled={sending} onClick={() => setPlacingNotFound(true)}>
                {label('website.not_found_placed')}
              </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Placing replaces whatever stood here, so there is one button rather than a list with an Add. That is the
              one arrangement shape the shared placement control does not draw — it draws ordered lists — which is why
              this screen draws it.
            </Typography>
          </Stack>
        </Paper>
      </Section>

      <Section
        title="Slot defaults"
        caption="The header and footer every page inherits unless it declares its own. Drawn by the same control every other arrangement uses — the routing tree is a face of its own and the 404 is drawn above, so both are hidden from it."
      >
        {/* The tree and the selected node's editor, the layout both
            other build screens use. Until this panel was here a block
            placed into a site's header could be arranged and never
            filled in — the site's defaults were the last tree in this
            register with no way to edit what it had selected. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(360px, 3fr)' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <Box>
            <CompositionEditor
              tenant={tenant}
              document={document}
              manifest={manifest.data}
              selection={selection}
              onSelect={select}
              send={send}
              hides={['routing', 'notFound']}
              busy={sending}
            />
          </Box>

          <NodePanel
            tenant={tenant}
            manifest={manifest.data}
            selection={selection}
            selected={selected}
            document={document}
            onSelect={select}
            send={send}
            busy={sending}
            editsRoot={false}
            empty={
              <Typography variant="caption" color="text.secondary">
                Select a placement to edit it. The site&rsquo;s own fields are on the Settings tab.
              </Typography>
            }
          />
        </Box>
      </Section>
        </>
      )}

      {face === 'settings' && (
        <>
      <Section title="Web addresses" caption="The addresses this site answers on. One is the main address, and every other one redirects to it.">
        <WebAddresses
          tenant={tenant}
          site={site}
          domains={fields.domains}
          bind={intentIn(SECTIONS.domains).find((intent) => intent.name === 'website.domain_bound')}
          release={intentIn(SECTIONS.domains).find((intent) => intent.name === 'website.domain_released')}
          label={label}
          busy={sending}
          send={sendIntent}
        />
      </Section>

      <Section title="Site" caption="The site's name and title, its theme, and the search-engine details every page starts from.">
        <Paper sx={{ p: 2.5 }}>
          <Stack spacing={3}>
            {intentIn(SECTIONS.identity).map((intent) => (
              <IntentForm
                key={intent.name}
                intent={intent}
                fields={fields as Record<string, unknown>}
                busy={sending}
                send={sendIntent}
              />
            ))}
          </Stack>
        </Paper>
      </Section>

      {/* The safety net on naming intents by hand: whatever this type
          declares that no section above claimed is drawn anyway. A
          sixteenth Website intent appears the day it is registered
          instead of being quietly unreachable, which is the failure the
          rest of this client is built to make impossible. */}
      {unclaimed.length > 0 && (
        <Section
          title="Other settings"
          caption="Settings this screen has no section of its own for."
        >
          <Paper sx={{ p: 2.5 }}>
            <Stack spacing={3}>
              {unclaimed.map((intent) => (
                <IntentForm
                  key={intent.name}
                  intent={intent}
                  fields={fields as Record<string, unknown>}
                  busy={sending}
                  send={sendIntent}
                />
              ))}
            </Stack>
          </Paper>
        </Section>
      )}
        </>
      )}

      {/* The 404's own picker. It is here rather than in the shared
          control because `website.not_found_placed` and
          `website.page_added` are the same shape — a child, no slot —
          and only this screen knows which arrangement each one fills. */}
      <PlacementPicker
        tenant={tenant}
        into={placingNotFound ? 'not found' : null}
        palette={containable(manifest.data, typeName)}
        manifest={manifest.data}
        onClose={() => setPlacingNotFound(false)}
        onLink={(targetId) => {
          const taken = new Set(allLinkIds(document.children))
          placeNotFound({ linkId: mintNotFoundLinkId(targetId, taken), id: payloadId(tenant, targetId) })
        }}
        onInline={(typeName, fields) => {
          const taken = new Set(allLinkIds(document.children))
          placeNotFound({ linkId: mintNotFoundLinkId(typeName.toLowerCase(), taken), type: typeName, fields })
        }}
        busy={sending}
      />

      {routeDialog?.intent === 'website.generated_route_created' || routeDialog?.intent === 'website.generated_route_updated' ? (
        <GeneratedRouteDialog
          tenant={tenant}
          manifest={manifest.data}
          references={document.references ?? []}
          sourceTypes={typeOf(manifest.data, typeName)?.references?.find((reference) => reference.name === 'routeSource')?.accepts ?? []}
          patternTypes={typeOf(manifest.data, typeName)?.references?.find((reference) => reference.name === 'routePattern')?.accepts ?? []}
          under={routeDialog.intent === 'website.generated_route_created' ? routeDialog.under : routeDialog.route.under}
          parentPath={
            routeDialog.intent === 'website.generated_route_created'
              ? routeDialog.parentPath
              : routeDialog.route.urlPath.replace(/\/:[^/]+$/, '') || '/'
          }
          position={routeDialog.intent === 'website.generated_route_created' ? routeDialog.position : 0}
          menuOptions={menusInUse(websiteFields(document).routing?.nodes)}
          route={routeDialog.intent === 'website.generated_route_updated' ? routeDialog.route.route : undefined}
          onClose={() => setRouteDialog(null)}
          onSend={sendIntent}
          busy={sending}
        />
      ) : routeDialog && (
        <RouteDialog
          tenant={tenant}
          document={document}
          intent={structural.get(routeDialog.intent)}
          route={routeDialog.intent === 'website.route_updated' ? routeValues(routeDialog.route) : undefined}
          under={routeDialog.intent === 'website.page_added' ? routeDialog.under : undefined}
          position={routeDialog.intent === 'website.page_added' ? routeDialog.position : undefined}
          onClose={() => setRouteDialog(null)}
          onSend={sendIntent}
          busy={sending}
        />
      )}
    </Stack>
  )
}

/**
 * The site's web addresses: the ones it answers on, and a choice among
 * the ones the account holds.
 *
 * **The author picks; nobody types a host.** A host is the platform
 * operator's to grant to a tenant, so the only hosts worth offering are
 * the ones granted and not already answering for another site — anything
 * else the platform refuses. A host already on this site is offered too,
 * because binding it again with the switch on is how the main address
 * moves.
 *
 * Bespoke for the reason the rest of this screen is, and narrower: a
 * derived form over `website.domain_bound` would draw a text box for the
 * host, which is a box over a value the platform refuses for every host
 * the operator has not granted. The two intents are still the type's, and
 * the buttons still carry the type's own words for them.
 */
function WebAddresses({
  tenant,
  site,
  domains,
  bind,
  release,
  label,
  busy,
  send,
}: {
  tenant: string
  site: string
  domains: WebsiteDomain[] | undefined
  bind: ManifestIntent | undefined
  release: ManifestIntent | undefined
  label: (intent: string) => string
  busy: boolean
  send: (intent: string, payload: Record<string, unknown>) => Promise<unknown>
}) {
  // Re-read on arrival: a grant is made on another surface, often a
  // minute before the author opens this face, and the listing is small.
  const listing = useListTenantDomainsQuery({ tenant }, { refetchOnMountOrArgChange: true })
  const addresses = siteAddresses(site, domains, listing.data?.domains)
  const [host, setHost] = useState('')
  const [main, setMain] = useState<boolean | undefined>(undefined)
  const chosen = addresses.offered.some((offered) => offered.host === host) ? host : ''
  const makeMain = main ?? mainByDefault(addresses, chosen || undefined)

  const add = () => {
    if (!bind || !chosen) return
    send(bind.name, { host: chosen, canonical: makeMain })
      .then(() => {
        setHost('')
        setMain(undefined)
      })
      .catch(shown)
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        {addresses.stated.length > 0 ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {addresses.stated.map((address) => (
              <Stack key={address.host} direction="row" spacing={0.75} alignItems="center">
                <Chip
                  size="small"
                  variant={address.main ? 'filled' : 'outlined'}
                  color={address.heldElsewhere ? 'warning' : 'default'}
                  icon={address.heldElsewhere ? <WarningAmberIcon /> : undefined}
                  label={address.main ? `${address.host} · main address` : address.host}
                  disabled={busy}
                  onDelete={release ? () => send(release.name, { host: address.host }).catch(shown) : undefined}
                  // Named, so the delete is a control a screen reader can
                  // find rather than a glyph inside one.
                  deleteIcon={
                    release && (
                      <CancelIcon role="button" aria-hidden={false} aria-label={`${label(release.name)}: ${address.host}`} />
                    )
                  }
                />
                {address.heldElsewhere && (
                  <Typography variant="caption" color="warning.main">
                    This address is held by another site
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        ) : (
          addresses.offered.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              No web address is set up yet.
            </Typography>
          )
        )}

        {listing.isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Reading this account&rsquo;s web addresses…
          </Typography>
        ) : listing.error ? (
          <Alert severity="error">{problemMessage(listing.error, 'The web addresses on this account could not be read.')}</Alert>
        ) : addresses.offered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {addresses.anyGranted
              ? 'Every web address on this account is in use on another site. Ask Bluu to add one.'
              : 'No web addresses have been set up for this account yet. Ask Bluu to add one.'}
          </Typography>
        ) : (
          bind && (
            <Stack spacing={1.5}>
              <Typography variant="h3">Add a web address</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField
                  select
                  size="small"
                  label="Web address"
                  value={chosen}
                  onChange={(event) => {
                    setHost(event.target.value)
                    setMain(undefined)
                  }}
                  sx={{ minWidth: 280 }}
                >
                  {addresses.offered.map((offered) => (
                    <MenuItem key={offered.host} value={offered.host}>
                      {offered.onThisSite ? `${offered.host} · already on this site` : offered.host}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={<Switch checked={makeMain} onChange={(event) => setMain(event.target.checked)} />}
                  label="Make this the main address"
                />
              </Stack>
              <Stack direction="row">
                <Button variant="contained" size="small" disabled={busy || !chosen} onClick={add}>
                  {label(bind.name)}
                </Button>
              </Stack>
            </Stack>
          )
        )}
      </Stack>
    </Paper>
  )
}

/**
 * A route's current values, under the names `website.route_updated`
 * carries them.
 *
 * They agree with the stored `Route` because both are read off the same
 * Go struct field — the same rule `storedValue` relies on to prefill any
 * generated form.
 */
function routeValues(route: ResolvedRoute): Record<string, unknown> {
  return {
    page: route.route.page,
    slug: route.route.slug,
    title: route.route.title ?? '',
    description: route.route.description ?? '',
    menus: routeMenus(route.route),
  }
}

function Section({ title, caption, children }: { title: string; caption?: string; children: React.ReactNode }) {
  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.5}>
        <Typography variant="h2">{title}</Typography>
        {caption && (
          <Typography variant="body2" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Stack>
      {children}
    </Stack>
  )
}

/** The same minting rule the placement picker uses, for the same reason. */
function mintNotFoundLinkId(seed: string, taken: Set<string>): string {
  const base = seed.replace(/[^a-zA-Z0-9-]/g, '-').slice(0, 24) || 'notfound'
  if (!taken.has(base)) return base
  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${base}-${suffix}`
    if (!taken.has(candidate)) return candidate
  }
}
